import { adminService } from "@/api/admin/admin.service";
import type {
  TAiChatLog,
  TAiContext,
  TAiModelStats,
  TAiUsageStats,
} from "@/api/admin/admin.types";
import {
  DataGrid,
  type TColumnDef,
  type TDataGridQuery,
  type TFilterDef,
} from "@/components/data-grid";
import { Button } from "@/components/ui/Button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/Skeleton";
import { RefreshCw } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

const PAGE_SIZE = 20;

function StatCard({ title, value }: { title: string; value: string | number }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <p className="text-sm font-medium text-muted-foreground">{title}</p>
        <p className="mt-1 text-2xl font-bold text-foreground">{value}</p>
      </CardContent>
    </Card>
  );
}

export function AiUsagePage() {
  const [stats, setStats] = useState<TAiUsageStats | null>(null);
  const [modelStats, setModelStats] = useState<TAiModelStats | null>(null);
  const [contexts, setContexts] = useState<TAiContext[]>([]);
  const [statsLoading, setStatsLoading] = useState(true);
  const [reloadTick, setReloadTick] = useState(0);

  const loadStats = useCallback(async (signal: AbortSignal) => {
    setStatsLoading(true);
    const [statsRes, modelRes, ctxRes] = await Promise.all([
      adminService.getAiUsageStats({ signal }),
      adminService.getAiModelStats({ signal }),
      adminService.getAiContexts({ limit: 50, offset: 0 }, { signal }),
    ]);
    if (signal.aborted) return;
    if (!statsRes.error && statsRes.data) setStats(statsRes.data.data ?? null);
    if (!modelRes.error && modelRes.data) setModelStats(modelRes.data.data ?? null);
    if (!ctxRes.error && ctxRes.data) setContexts(ctxRes.data.data ?? []);
    setStatsLoading(false);
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    void loadStats(ctrl.signal);
    return () => ctrl.abort();
  }, [loadStats]);

  const fetcher = async (query: TDataGridQuery) => {
    const response = await adminService.getAiUsageLogs(
      {
        limit: query.limit,
        offset: query.offset,
        ...(query.search ? { search: query.search } : {}),
        ...(query.sort_by ? { sort_by: query.sort_by, sort_order: query.sort_order } : {}),
        ...(typeof query.filters.model_name === "string" && query.filters.model_name
          ? { model_name: query.filters.model_name }
          : {}),
        ...(typeof query.filters.company_id === "string" && query.filters.company_id
          ? { company_id: query.filters.company_id }
          : {}),
      },
      { signal: query.signal }
    );
    if (response.error) throw response.error;
    return response.data;
  };

  const columns: TColumnDef<TAiChatLog>[] = [
    { id: "ai_provider", header: "Provider", type: "badge" },
    {
      id: "total_tokens",
      header: "Tokens",
      align: "right",
      sortable: true,
      sortKey: "total_tokens",
      accessor: (l) => l.total_tokens ?? null,
    },
    {
      id: "total_cost",
      header: "Cost",
      type: "currency",
      align: "right",
      sortable: true,
      sortKey: "total_cost",
      meta: { fractionDigits: 4 },
      accessor: (l) => l.total_cost ?? null,
    },
    {
      id: "was_successful",
      header: "Status",
      type: "status",
      accessor: (l) => (l.was_successful ? "Success" : "Failed"),
    },
    {
      id: "thread_id",
      header: "Thread",
      type: "uuid",
      hideable: true,
      accessor: (l) => l.thread_id ?? "",
    },
    { id: "created_at", header: "Created", type: "date", sortable: true, sortKey: "created_at" },
  ];

  const filters: TFilterDef[] = [
    { id: "model_name", type: "text", label: "Provider / model", placeholder: "Provider / model name" },
    { id: "company_id", type: "text", label: "Company ID", placeholder: "Company ID" },
  ];

  const modelsUsedCount = Array.isArray(modelStats?.models_used) ? modelStats?.models_used.length : 0;

  return (
    <div className="h-full overflow-y-auto bg-background p-4 sm:p-6">
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground sm:text-3xl">AI Usage</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Browse AI request stats, model usage, and per-request logs
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const ctrl = new AbortController();
              void loadStats(ctrl.signal);
              setReloadTick((t) => t + 1);
            }}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>

        {/* Stats */}
        {statsLoading ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-24 rounded-xl" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <StatCard title="Total Requests" value={stats?.total_requests ?? 0} />
            <StatCard title="Success Rate" value={`${(stats?.success_rate ?? 0).toFixed(0)}%`} />
            <StatCard title="Total Tokens" value={stats?.total_tokens ?? 0} />
            <StatCard title="Total Cost (USD)" value={`$${(stats?.total_cost_usd ?? 0).toFixed(2)}`} />
          </div>
        )}
        <p className="text-xs text-muted-foreground">
          Models tracked: {modelsUsedCount} · Active contexts sampled: {contexts.length}
        </p>

        <DataGrid<TAiChatLog>
          getRowId={(row) => row.id}
          persistKey="admin.ai-usage"
          ariaLabel="AI usage logs"
          data={{ mode: "server", fetcher, deps: [reloadTick] }}
          columns={columns}
          filters={filters}
          pagination={{ mode: "offset", pageSize: PAGE_SIZE, pageSizeOptions: [10, 20, 50, 100] }}
          toolbar={{
            search: true,
            refresh: true,
            columnVisibility: true,
            density: true,
            export: ["csv", "json"],
            searchPlaceholder: "Search provider",
          }}
          emptyState={{ title: "No AI usage logs found." }}
        />
      </div>
    </div>
  );
}
